import { useEffect, useState } from 'react';
import { SparklesIcon } from 'lucide-react';
import type { ChangelogItem, ListChangelogResponse } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError, apiGet } from '../api/client.js';

/**
 * "Wat is nieuw" — de changelog voor iedereen (issue #238).
 *
 * Omgekeerd chronologisch; de backend levert al gesorteerd en zonder
 * concepten. Geen zoekveld en geen gelezen-markering: de bel doet dat werk.
 */

const DATUM = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Amsterdam',
});

function toonDatum(date: string): string {
  // `date` is een wall-clock datum (YYYY-MM-DD); T12:00 houdt hem in de
  // NL-zomertijd op dezelfde dag als de beheerder hem intypte.
  return DATUM.format(new Date(`${date}T12:00:00`));
}

export function WatIsNieuwPage(): JSX.Element {
  const [items, setItems] = useState<ChangelogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiGet<ListChangelogResponse>('/api/v1/changelog');
        setItems(resp.items);
      } catch (err) {
        setErrorMsg(
          err instanceof ApiError
            ? `Laden mislukt (${err.status}): ${err.message}`
            : 'Wat is nieuw kon niet geladen worden.',
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <header className="mb-6">
          <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
            <SparklesIcon className="h-5 w-5 text-brand-700" aria-hidden />
            Wat is nieuw
          </h1>
          <p className="mt-1 text-sm text-ink/70">
            Hier lees je wat er is bijgekomen of veranderd in de Kennishub. Je krijgt een melding in
            de bel zodra er iets bij komt.
          </p>
        </header>

        {errorMsg && (
          <div className="mb-4 rounded-2xl border border-error/30 bg-error/5 px-4 py-3 text-sm text-error">
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-10 text-center text-sm text-ink/55">
            Laden…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-10 text-center text-sm text-ink/55">
            Er is nog niets om te melden. Zodra er iets verandert, lees je het hier.
          </div>
        ) : (
          <ol className="flex flex-col gap-3">
            {items.map((item) => (
              <li
                key={item.id}
                className="rounded-2xl border border-ink-100 bg-white px-5 py-4 shadow-card"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                  {toonDatum(item.date)}
                </p>
                <h2 className="mt-1 text-base font-bold text-ink-900">{item.title}</h2>
                <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink">
                  {item.body}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Layout>
  );
}
